import ContextedReduceStore from "data/util/ContextedReduceStore";
import Dispatcher from "Dispatcher";
import AbstractionAiActions from "./AbstractionAiActions";
import dotProp from "dot-prop-immutable";
import LoadObject from "data/LoadObject";
import SuperRecordStore from "data/SuperRecordStore";
import NavigatorStore from "data/NavigatorStore";
import TableStore from "data/TableStore";
import DataManager from "data/DataManager";
import userStore from "data/userStore";
import invariant from "invariant";
import { dehydrateField } from "data/util/hydration";
import { FIRST_FIELD } from "data/ActiveFormStore";
import ActiveFormActions from "data/ActiveFormActions";
import RecordActions from "data/RecordActions";
import tableStore from "../TableStore";
import entityMetaStore from "./entityMetaStore";
import EntityName from "data/util/EntityName";
import AbstractionAIService from "lib/AbstractionAIService";

const keyPath = (entityKey, fieldKey) => {
    if (typeof entityKey !== "string") {
        // assume it's an action
        const a = entityKey;
        entityKey = a.entityKey || a.recordKey;
        fieldKey = a.fieldKey;
    }
    return `${entityKey}.${fieldKey}`;
};

const getRecordAiSuggestFieldKeys = (recordsPath) => {
    const fields = [];
    entityMetaStore.getAllInfos((it) =>
        (it.keys || []).filter((key) => key.name === "ai-suggestion").length && // only ai-suggestKeys
        recordsPath.filter((r) => it.key.indexOf(r.key.split("/")[0]) === 0).length // only currently open records path
    ).forEach((it) => {
        (it.keys || []).filter((key) => key.name === "ai-suggestion").forEach((field) => {
            fields.push({
                key: it.key,
                type: it.type,
                fieldName: field.fieldKeys[0],
            });
        });
    });
    return fields;
};

const checkIfAcceptedSuggestion = (state, action) => {
    //  if field has suggestion
    //  see if set value matches any ai-suggestions
    //  value must first be converted to its label
    //  log accept
    const entityKey = action.entityKey || action.recordKey;
    const fieldKey = action.fieldKey;
    const lo = state[keyPath(entityKey, fieldKey)];
    if(lo && lo.hasValue() && lo.getRequiredValue()) {
        const data = lo.getRequiredValue();
        if(
            // do we have all the suggestion info we need?
            data.status === "SUCCESS"
            && data.suggestion
            && data.suggestion.fieldSuggestion
            && data.suggestion.fieldSuggestion.length
        ) {
            const fieldSuggestionId = data.suggestion.fieldSuggestionId;
            const suggestions = ((data.suggestion || {}).fieldSuggestion || []).map(
                it => it.toString().toLowerCase() // make all answers lowercase
            );
            const label = (action.label || action.value);
            const acceptIndices = [];
            if (label !== null) {
                if (Array.isArray(label)) { // multi valued
                    label.forEach(it => {
                        const index = suggestions.indexOf(it.toString().toLowerCase()); // make all labels lowercase
                        if (index !== -1) {
                            acceptIndices.push(index);
                        }
                    });
                } else {
                    const index = suggestions.indexOf(label.toString().toLowerCase()); // make all labels lowercase
                    if (index !== -1) {
                        acceptIndices.push(index);
                    }
                }
                if (acceptIndices.length) {
                    // user selected ai-suggest recommendation(s)
                    AbstractionAIService.sendAccept(
                        fieldSuggestionId,
                        acceptIndices
                    );
                }
            }
        }
    }
};

const getAiSuggestKeyInfo = (fluxContext) => {
    // Finds GLOBAL and RECORD field keys `ai-suggestion` and their values.
    // `ai-suggestion` keys found in EntityMetaStore
    // create a payload shaped like this: [{"value":"mylastname", entityType:"GLOBAL"}]
    // EntityMetaStore contains all metadata impls not just the ones
    // associated with the currently open record. We need to only find the values of the currently open
    // record(s) this means we need to know the current open record and its parent(s) as show in the
    // selected navigator columns.
    const selectedRecords = NavigatorStore.getRecordPath(fluxContext);
    let hasRequiredValues = true;
    const keyComponents = [];
    const payload = getRecordAiSuggestFieldKeys(selectedRecords).map((it) => {
        const key = it.key;
        const fieldName = it.fieldName;
        const activeRecord = selectedRecords.find((r) => r.key === key);
        let id = null;
        let value = null;
        if (activeRecord) {
            id = activeRecord.id;
            const ep = TableStore.getEntityProxy(key, id);
            const fieldType = ep.getAttributeType(fieldName);
            value = ep.get(fieldName); // hydrated value
            if(value != null) {
                switch (fieldType) {
                    case "DATETIME":
                    case "TIME":
                    case "DATE":
                    //case "TZTIMESTAMP": not sure what TZTIMESTAMP is?
                        // server needs string version of date "YYYY-MM-DD"
                        // convert moment to that format.
                        value = value.format("YYYY-MM-DD");
                        break;
                    default:
                        value = dehydrateField(value, fieldType); // dehydrated value
                    // do nothing
                }
            }
        }
        const [implementation, version, entityName] = key.split("/");
        if (!value) {
            hasRequiredValues = false;
            keyComponents.push({
                implementation,
                version,
                entityName,
                id,
                fieldName,
            });
        }
        // return payload item shape
        return {
            value: value,
            entityType: it.type === "ROOT" ? "RECORD" : it.type //
        };
    }).filter((it) => it !== null);
    return {
        keyComponents,
        hasRequiredValues,
        payload,
    };
};

class AbstractionAiStore extends ContextedReduceStore {

    constructor() {
        super(Dispatcher);
    }

    getInitialContext() {
        return {
            feedback: {}
        };
    }

    reduceContext(state, action, fluxContext) {
        switch (action.type) {
            case ActiveFormActions.FOCUS_ELEMENT:
            case RecordActions.SET_FIELD:
                this.__dispatcher.waitFor([
                    userStore.getDispatchToken(),
                    tableStore.getDispatchToken(),
                    entityMetaStore.getDispatchToken(),
                    NavigatorStore.getDispatchToken(),
                    SuperRecordStore.getDispatchToken(),
                ]);
                try {
                    // none of this functionality is critical to the applications functionality and can be ignored if
                    // any of it fails for some reason.
                    if(action.type === RecordActions.SET_FIELD) {
                        checkIfAcceptedSuggestion(state, action);
                    }
                    const {
                        keyComponents,
                        hasRequiredValues,
                        payload
                    } = getAiSuggestKeyInfo(fluxContext);

                    if (!hasRequiredValues || payload.length < 2) {
                        // cant make the request yet because required values are not met
                        return dotProp.set(state, [keyPath(action)], LoadObject.withValue({
                            // fake api response for INCOMPLETE_SUGGEST_KEY
                            status: "INCOMPLETE_SUGGEST_KEY",
                            suggestion: {answerLabels: []},
                            keyComponents: keyComponents,
                        }));
                    }
                    // we have everything we need to make the full ai-suggest request :)
                    return dotProp.set(state, [keyPath(action)], LoadObject.empty().loading(
                        DataManager.loadAbstractionAi(
                            userStore.getActiveOrgKey(),
                            action.entityKey || action.recordKey,
                            action.fieldKey,
                            payload
                        )
                    ));
                }
                catch (error) {
                    // allow the app to continue to function. but log the error to the console.
                    console.warn("A problem occured while processing the AbstractionAiStore.", error);
                    return state;
                }
            case AbstractionAiActions.FEEDBACK:
                // log the feedback
                AbstractionAIService.sendFeedback(action.evidenceId, action.feedback);
                return {
                    ...state,
                    feedback: {
                        ...(state.feedback || {}),
                        [action.evidenceId]: action.feedback
                    }
                };
            default:
                return state;
        }
    }

    reduceEachContext(state, action, fluxContext) {
        switch(action.type) {
            case AbstractionAiActions.LOADED:
                if(!dotProp.get(state, [keyPath(action)])) {
                   return state;
                }
                const data = action.data;
                if(data.suggestion && data.suggestion.fieldSuggestionId) {
                    // log an impression
                    AbstractionAIService.sendImpression(
                        this.hasAISuggestionPermission(fluxContext),
                        data.suggestion.fieldSuggestionId
                    );
                }
                return dotProp.set(state, [keyPath(action)], lo => lo.receiveValue(data));
            case AbstractionAiActions.LOAD_ERROR:
                if(!dotProp.get(state, [keyPath(action)])) {
                   return state;
                }
                return dotProp.set(state, [keyPath(action)], lo => lo.receiveError(action.error));
            default:
                return state;
        }
    }

    hasAISuggestionPermission(fluxContext) {
        const activeRecord = NavigatorStore.getRecordState(fluxContext);
        if(!activeRecord) {
            return false;
        }
        const implKey = activeRecord && activeRecord.key ? new EntityName(activeRecord.key).implKey : false;
        if(!implKey) {
            return false;
        }
        return userStore.canAISuggestion(implKey);
    }

    hasAISuggestionKeys(fluxContext) {
        const activeRecord = NavigatorStore.getRecordState(fluxContext);
        if(!activeRecord) {
            return false;
        }
        // check if open record has any ai-suggest keys
        return getRecordAiSuggestFieldKeys([activeRecord]).length > 0;
    }

    canAISuggestion(fluxContext) {
        return this.hasAISuggestionKeys(fluxContext) && this.hasAISuggestionPermission(fluxContext);
    }
    getAbstractionAiSuggest(ctx, entityKey, fieldKey) {
        return this.getAbstractionAiSuggestLO(entityKey, fieldKey).getRequiredValue();
    }

    getAbstractionAiSuggestLO(ctx, entityKey, fieldKey) {
        invariant(
            entityKey && fieldKey && fieldKey !== FIRST_FIELD,
            `C'mon, field '${entityKey}.${fieldKey}'? Really?`
        );
        return this.getState(ctx)[keyPath(entityKey, fieldKey)] || null;
    }

    getFeedback(ctx, evidenceId) {
        return this.getState(ctx).feedback[evidenceId];
    }
}

export default new AbstractionAiStore();



// WEBPACK FOOTER //
// ./app/data/meta/AbstractionAiStore.js